// src/contexts/GpnBudgetPurchasesContext.jsx
//
// Admin side of GEFEN budget-purchase reconciliation: list purchase
// requests (filterable by status/method) and confirm a pending bank
// transfer once the admin has actually seen it land in the bank
// statement, capturing the real transfer details for the audit trail.
// Mirrors SchoolProgramsContext.jsx's shape/pattern exactly (same
// apiFetch helper, same conventions), just pointed at
// /api/admin/gpn/budget-purchases/*.
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = 'https://www.doroosonline.co.il';
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const DEFAULT_TIMEOUT_MS = 12000;

const getStoredToken = () => {
  try {
    return localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null;
  } catch {
    return null;
  }
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function apiFetch(path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const token = getStoredToken();
  const hasBody = body !== undefined;

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(body) : undefined,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    const data = await parseJSONSafely(res);

    if (!res.ok) {
      const msg =
        data?.error ||
        (typeof data?.error === 'object' ? Object.values(data.error)[0] : null) ||
        (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
      throw new Error(msg);
    }

    return data ?? {};
  } catch (e) {
    if (e.name === 'AbortError') throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב.');
    if (e instanceof TypeError) {
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת ב- ${API_BASE}.`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

const GpnBudgetPurchasesContext = createContext(null);

export const GpnBudgetPurchasesProvider = ({ children }) => {
  const { role, userType } = useUser();
  const isAdmin = role === 'admin' || userType === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPurchases = useCallback(async ({ status, method } = {}) => {
    if (!isAdmin) return [];
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (method) params.set('method', method);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await apiFetch(`/api/admin/gpn/budget-purchases${qs}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
      return list;
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  // Fetches the uploaded receipt PDF as a blob (the endpoint is
  // JWT-protected, so a plain <a href> can't carry the Authorization
  // header) and opens it in a new tab. Caller is responsible for
  // nothing further — the object URL is only used once, immediately.
  const viewReceipt = useCallback(async (purchaseId) => {
    const token = getStoredToken();
    const res = await fetch(url(`/api/admin/gpn/budget-purchases/${purchaseId}/receipt`), {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) {
      const data = await parseJSONSafely(res);
      throw new Error(data?.error || `שגיאת שרת (${res.status})`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener');
    // Revoke after a delay long enough for the new tab to load it.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }, []);

  // transferDetails: { transfer_date: 'YYYY-MM-DD', payer_name, bank_reference, admin_note? }
  const confirmBankTransfer = useCallback(async (purchaseId, transferDetails) => {
    const data = await apiFetch(`/api/admin/gpn/budget-purchases/${purchaseId}/confirm`, {
      method: 'POST',
      body: transferDetails,
    });
    setItems((prev) => prev.map((p) => (
      p.id === purchaseId ? { ...p, status: 'PAID', ...transferDetails } : p
    )));
    return data;
  }, []);

  const value = useMemo(
    () => ({ items, loading, fetchPurchases, confirmBankTransfer, viewReceipt }),
    [items, loading, fetchPurchases, confirmBankTransfer, viewReceipt]
  );

  return (
    <GpnBudgetPurchasesContext.Provider value={value}>
      {children}
    </GpnBudgetPurchasesContext.Provider>
  );
};

export const useGpnBudgetPurchases = () => {
  const ctx = useContext(GpnBudgetPurchasesContext);
  if (!ctx) throw new Error('useGpnBudgetPurchases must be used within a GpnBudgetPurchasesProvider');
  return ctx;
};